"use client";

import { CalendarDays, Anchor, Utensils, BedDouble } from "lucide-react";
import { formatConsistentDate, daysBetweenDateStrings } from "@/utils/timezone-date-utils";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iCruiseLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";

export interface CruiseItineraryInnerProps extends iCruiseLegDetails {
  primaryColor?: string;
  day?: number;
}

export function CruiseItineraryInner(props: CruiseItineraryInnerProps) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  const getDurationInNights = () => {
    if (!props.departureDate || !props.arrivalDate) return 0;
    return daysBetweenDateStrings(props.departureDate, props.arrivalDate);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Cruise Header */}
      {(props.cruiseLine ||
        props.shipName ||
        props.cabinType ||
        props.boardBasis) && (
        <div>
          {props.cruiseLine && (
            <h3 className="text-lg sm:text-xl font-semibold mb-2">{props.cruiseLine}</h3>
          )}
          {props.shipName && (
            <p className="text-sm sm:text-base text-gray-600">{props.shipName}</p>
          )}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 sm:mt-3">
            {props.cabinType && (
              <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                <BedDouble
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  style={{ color: primaryColor }}
                />
                <span className="hidden sm:inline">{props.cabinType}</span>
                <span className="sm:hidden">{props.cabinType.split(" ")[0]}</span>
              </span>
            )}
            {props.boardBasis && (
              <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                <Utensils
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  style={{ color: primaryColor }}
                />
                <span className="hidden sm:inline">{props.boardBasis}</span>
                <span className="sm:hidden">{props.boardBasis.split(" ")[0]}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Overview/Body */}
      {props.body && (
        <HtmlContentWithReadMore html={props.body} maxWords={40} />
      )}

      {/* Gallery */}
      {props.gallery && props.gallery.length > 0 && (
        <div className="mt-4 sm:mt-6">
          <ImageGallery images={props.gallery} />
        </div>
      )}

      {/* Cruise Details */}
      {(props.departureDate ||
        props.departurePort ||
        props.arrivalDate ||
        props.arrivalPort) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Departure */}
          {(props.departureDate || props.departurePort) && (
            <div className="space-y-3 sm:space-y-4">
              {props.departureDate && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <CalendarDays
                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
                    style={{ color: primaryColor }}
                  />
                  <div>
                    <div className="font-medium text-sm sm:text-base">
                      <span className="hidden sm:inline">Departure</span>
                      <span className="sm:hidden">Dep</span>
                    </div>
                    <div className="text-sm sm:text-base">{formatDate(props.departureDate)}</div>
                  </div>
                </div>
              )}
              {props.departurePort && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <Anchor
                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
                    style={{ color: primaryColor }}
                  />
                  <div>
                    <div className="font-medium text-sm sm:text-base">
                      <span className="hidden sm:inline">Port</span>
                      <span className="sm:hidden">Port</span>
                    </div>
                    <div className="text-sm text-gray-600">{props.departurePort}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Arrival */}
          {(props.arrivalDate || props.arrivalPort) && (
            <div className="space-y-3 sm:space-y-4">
              {props.arrivalDate && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <CalendarDays
                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
                    style={{ color: primaryColor }}
                  />
                  <div>
                    <div className="font-medium text-sm sm:text-base">
                      <span className="hidden sm:inline">Arrival</span>
                      <span className="sm:hidden">Arr</span>
                    </div>
                    <div className="text-sm sm:text-base">{formatDate(props.arrivalDate)}</div>
                  </div>
                </div>
              )}
              {props.arrivalPort && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <Anchor
                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
                    style={{ color: primaryColor }}
                  />
                  <div>
                    <div className="font-medium text-sm sm:text-base">
                      <span className="hidden sm:inline">Port</span>
                      <span className="sm:hidden">Port</span>
                    </div>
                    <div className="text-sm text-gray-600">{props.arrivalPort}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duration Badge */}
      {getDurationInNights() > 0 && (
        <div>
          <span
            className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
            style={{
              backgroundColor: lightColor,
              color: primaryColor,
            }}
          >
            <span className="hidden sm:inline">{getDurationInNights()} nights cruise</span>
            <span className="sm:hidden">{getDurationInNights()} nights</span>
          </span>
        </div>
      )}
    </div>
  );
}
